import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { PageListService } from './page-list.service';
import { ApiLiffHomeMenuService } from './api/LiffHome/api-liff-home-menu';
import {
  LiffHomeMenuConfig,
  LiffHomeMenuConfigResponse,
  LiffHomeMenuItemConfig,
  LiffMenuVisibility,
} from '../models/liff-home-menu.model';

export interface LiffHomeResolvedMenus {
  quick: any[];
  more: any[];
  /** true = จาก API, false = static PageList fallback */
  fromApi: boolean;
}

/**
 * Session cache for LIFF Home Quick/More menus.
 * Fetches once per browser tab / LIFF open; reuse until page is closed/reloaded.
 */
@Injectable({ providedIn: 'root' })
export class LiffHomeMenuStateService {
  private menus$: Observable<LiffHomeResolvedMenus> | null = null;
  private cached: LiffHomeResolvedMenus | null = null;

  constructor(
    private api: ApiLiffHomeMenuService,
    private page: PageListService,
  ) {}

  /** Already resolved in this session (sync peek). */
  get snapshot(): LiffHomeResolvedMenus | null {
    return this.cached;
  }

  /**
   * Load menus once for this session. Subsequent calls share the same Observable.
   */
  getMenus(): Observable<LiffHomeResolvedMenus> {
    if (!this.menus$) {
      this.menus$ = this.api.getLiffHomeMenuConfig().pipe(
        map((res: LiffHomeMenuConfigResponse) => {
          const config = (res?.config ?? res?.Config) as LiffHomeMenuConfig | undefined;
          if (config?.quick?.length) {
            return this.resolveFromConfig(config, true);
          }
          return this.resolveStatic(false);
        }),
        catchError((err) => {
          console.error('getLiffHomeMenuConfig failed — using static PageList (session cache)', err);
          return of(this.resolveStatic(false));
        }),
        tap((menus: LiffHomeResolvedMenus) => {
          this.cached = menus;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.menus$;
  }

  private resolveStatic(fromApi: boolean): LiffHomeResolvedMenus {
    const quick = [...(this.page.quickServiceList || [])];
    const more = (this.page.commonList || [])
      .filter((c: any) => c.categoryEN !== 'Home')
      .map((c: any) => ({
        ...c,
        list: [...(c.list || [])],
      }));
    return { quick, more, fromApi };
  }

  private resolveFromConfig(config: LiffHomeMenuConfig, fromApi: boolean): LiffHomeResolvedMenus {
    const quick = [...(config.quick || [])]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((item) => this.normalizeVisibility(item.visibility) !== 'hidden')
      .map((item) => this.mapConfigItemToPage(item));

    const morePin = quick.filter((x) => x.action === 'more' || x.route === 'more');
    const withoutPin = quick.filter((x) => x.action !== 'more' && x.route !== 'more');

    const more = [...(config.more || [])]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((cat) => {
        const list = [...(cat.items || [])]
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .filter((item) => this.normalizeVisibility(item.visibility) !== 'hidden')
          .map((item) => this.mapConfigItemToPage(item));
        return {
          categoryTH: cat.categoryTH,
          categoryEN: cat.categoryEN || cat.categoryTH,
          list,
        };
      })
      .filter((cat) => cat.list.length > 0);

    return {
      quick: [...withoutPin, ...morePin],
      more,
      fromApi,
    };
  }

  private mapConfigItemToPage(item: LiffHomeMenuItemConfig) {
    const visibility = this.normalizeVisibility(item.visibility);
    return {
      route: item.route,
      nameTH: item.nameTH,
      nameEN: item.nameEN || item.nameTH,
      image: item.image || '',
      imageSlide: item.imageSlide || item.image || '',
      imageActive: item.imageActive || item.image || '',
      alt: item.alt || item.key,
      disable: visibility === 'disabled',
      disableTextTH: item.disableTextTH || 'ปิดปรับปรุง',
      disableTextEN: item.disableTextEN || 'Coming soon',
      alert: item.alert || '',
      action: item.action || '',
    };
  }

  private normalizeVisibility(value?: string): LiffMenuVisibility {
    const v = (value || 'enabled').toLowerCase();
    if (v === 'disabled' || v === 'hidden') {
      return v;
    }
    return 'enabled';
  }
}
